import {Component, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {
    SearchResult,
    SearchResultFilterChoice,
    SearchResultItem,
    SearchTypeEnum
} from '../../models/searchresult.model';
import {SharedApiService} from '../../../shared/services/api.service';
import {slugify} from "../../../shared/util/slugify";
import {FormatterService} from "../../../shared/services/formatter.service";
import {AuthService} from "../../../shared/services/auth.service";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {AddToShoppingCart} from "../../../shared/models/add-to-shopping-cart.model";
import {ShoppingCartCountService} from "../../../shared/services/shopping_cart_count";
import {ToastrService} from "ngx-toastr";
import {TranslateService} from "@ngx-translate/core";
import {AlertService} from "../../../shared/services/alert.service";
import {Article} from '../../models/article.model';
import {Product} from '../../models/product.model ';
import {Hierarchy} from '../../models/hierarchy.model';
import {
    QuestionModalComponent
} from "../../../app/components/footer/components/question-modal/question-modal.component";
import {LoginModalComponent} from "../login-modal/login-modal.component";
import {EnvService} from "../../../shared/services/env.service";

@Component({
    selector: 'app-catalogue-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit {

    public searchResult: SearchResult<any>;
    public searchResultArticles: SearchResult<Article>;
    public searchResultProducts: SearchResult<Product>;
    public searchTerm: string = null;
    public q: string;

    public searchTypeEnum;

    public canOrder: boolean;

    public showLoadingScreen: boolean = false;

    public showFilters: boolean = false;

    public activeIds: string[] = [];

    constructor(
            private apiService: SharedApiService,
            private router: Router,
            private route: ActivatedRoute,
            private formatterService: FormatterService,
            private authService: AuthService,
            private modalService: NgbModal,
            private shoppingCartCountService: ShoppingCartCountService,
            private translateService: TranslateService,
            private toastrService: ToastrService,
            private alertService: AlertService,
            private envService: EnvService
    ) {
    }

    /**
     * Initialize data
     */
    public ngOnInit() {
        this.searchTypeEnum = SearchTypeEnum;

        this.canOrder = this.authService.hasRoleOrderer();

        // Get data from resolver
        this.route.data.subscribe(data => {
            this.showLoadingScreen = false;

            this.searchResult = SearchComponent.calcFirstLastItem(data.searchResult);
            this.searchResult.hierarchy.scope = (<any>Object).values(this.searchResult.hierarchy.scope);
            if (this.searchResult.searchType === this.searchTypeEnum.ARTICLES) {
                // we're listing articles
                this.searchResultArticles = SearchComponent.calcFirstLastItem<Article>(data.searchResult);
                this.searchResultProducts = undefined;
            } else {
                // we're listing products
                this.searchResultProducts = SearchComponent.calcFirstLastItem<Product>(data.searchResult);
                this.searchResultArticles = undefined;
            }

            if (this.canOrder && this.searchResult.searchType === this.searchTypeEnum.ARTICLES) {
                this.getPricesAndStock();

                this.apiService.getAssortmentArticles().subscribe(articles => {
                    articles.forEach(article => {
                        let item = this.searchResult.items.find(item => item.guid === article.guid);

                        if (item) {
                            item.inAssortment = true;
                        }
                    });
                });
            }

            let i = 0;
            for (let filter of this.searchResult.filters) {
                this.activeIds.push('filter-' + i);
                if (i === 1) {
                    break;
                }

                i++;
            }
            this.showFilters = true;
        });
    }

    public getFilters() {
        return this.searchResult.filters;
    }

    getHierarchyChoices(hierarchy: Hierarchy): SearchResultFilterChoice[] {
        if (!this.searchResult.hierarchy.filter) {
            return [];
        }

        return this.searchResult.hierarchy.filter.choices.filter(item => item.urlParameterValue === hierarchy.guid);
    }

    /**
     * Change search type
     * @param searchType
     */
    public changeSearchType(searchType: string) {
        if (this.searchResult.searchType === searchType) {
            return;
        }

        this.showLoadingScreen = true;

        this.searchResult.searchType = searchType;
        this.searchResult.pager.currentPage = 1;
        this.searchResult.items = [];

        this.search(true);
    }

    /**
     * Clear all filters
     */
    public clearFilters() {
        this.searchResult.filters.forEach(filter => {
            filter.choices.forEach(choice => {
                choice.active = false;
            })
        });
        this.searchResult.pager.currentPage = 1;
        this.searchResult.hierarchy.filter.choices.forEach(choice => choice.active = false);

        this.search(true);
    }

    public checkFilter() {
        this.searchResult.pager.currentPage = 1;

        this.search(true);
    }

    public deselectFirstParentHierarchy() {
        if (this.searchResult.hierarchy.parents.length === 0) {
            return;
        }

        const hierarchy = this.searchResult.hierarchy.parents[this.searchResult.hierarchy.parents.length - 1];
        this.getHierarchyChoices(hierarchy).forEach(option => option.active = false);
    }

    public deselectHierarchy(hierarchy: Hierarchy) {
        this.getHierarchyChoices(hierarchy).forEach(option => option.active = false);
    }

    public switchToParentHierarchy(hierarchy: Hierarchy) {
        // deselect all hierarchies on current level
        this.searchResult.hierarchy.filter.choices.forEach(choice => choice.active = false);

        // select the clicked hierarchy
        this.getHierarchyChoices(hierarchy).forEach(option => option.active = true);
    }

    /**
     * Search
     */
    public search(forceSwitch: boolean = false) {
        let urlParams = new URLSearchParams();

        // Add query name
        if (this.searchResult.query.state.value) {
            urlParams.append('q', this.searchResult.query.state.value);
        }

        if (forceSwitch) {
            urlParams.append('force_switch', '1');
        }

        // Add search type
        urlParams.append('search_type', this.searchResult.searchType);

        // Set default pager options
        urlParams.append('page', this.searchResult.pager.currentPage.toString());
        urlParams.append('per_page', this.searchResult.pager.itemsPerPage.toString());

        // Loop through filters and add checked choices
        this.searchResult.filters.forEach(filter => {
            filter.choices.forEach(choice => {
                if (choice.active) {
                    urlParams.append(choice.urlParameterKey + '[]', choice.urlParameterValue)
                }
            })
        });

        if (this.searchResult.hierarchy.filter) {
            // Loop through filters and add checked choices
            this.searchResult.hierarchy.filter.choices.forEach(choice => {
                if (choice.active) {
                    urlParams.append(choice.urlParameterKey + '[]', choice.urlParameterValue)
                }
            });
        }

        // Navigate to url
        this.router.navigateByUrl('/catalogue?' + urlParams.toString()).then();
    }

    public newSearch() {
        let urlParams = new URLSearchParams();
        urlParams.append('q', this.searchTerm);

        if (['MPA', 'Isolatec'].includes(this.envService.companyName)) {
            urlParams.append('force_switch', '1');
            urlParams.append('search_type', 'articles');
        }

        this.router.navigateByUrl('/catalogue?' + urlParams.toString()).then();
    }

    public getPricesAndStock() {
        for (let item of this.searchResult.items) {
            // todo, initially load prices
            item.hasVolumePrices = false;
            item.quantity = undefined;
        }
    }

    public slug(name: string) {
        return slugify(name);
    }

    public quantity(item: SearchResultItem): number {
        if (!item.quantity) {
            return 1;
        }

        return item.quantity;
    }

    public setQuantity(item: SearchResultItem, quantity?: number) {
        item.quantity = quantity;
    }

    public addToShoppingCart() {
        const articles = this.searchResult.items
                .filter(item => item.quantity > 0)
                .map(item => ({
                    guid: item.guid,
                    quantity: item.quantity,
                } as AddToShoppingCart));

        if (articles.length === 0) {
            this.alertService.alert(this.translateService.instant('global.notification.set_quantity'));
        } else {
            this.apiService.addToShoppingCart(articles).subscribe(result => {
                if ((result.length || 0) > 0) {
                    for (let item of this.searchResult.items) {
                        this.setQuantity(item, null);
                    }

                    this.shoppingCartCountService.updateCount();
                    this.toastrService.success(this.translateService.instant('page.assortment.added_to_shopping_cart'));
                }
            });
        }
    }

    /**
     * Calc first and last item from search result
     * @param searchResult
     * @private
     */
    private static calcFirstLastItem<T>(searchResult: SearchResult<any>): SearchResult<T> {
        searchResult.pager.firstItem = ((searchResult.pager.currentPage - 1) * searchResult.pager.itemsPerPage) + 1;

        const lastItem = searchResult.pager.currentPage * searchResult.pager.itemsPerPage;
        searchResult.pager.lastItem = lastItem < searchResult.pager.totalItems ? lastItem : searchResult.pager.totalItems;

        return searchResult;
    }

    // Show/hide filter bar on small
    public toggleFilterBar() {
        if (document.body.classList.contains('filter-sidebar-show')) {
            document.body.classList.remove('filter-sidebar-show');
        } else {
            document.body.classList.add('filter-sidebar-show');
        }
    }

    public addToFavorites(item: SearchResultItem) {
        item.inAssortment = true;

        this.apiService.addToAssortment([item.guid]).subscribe(() => {
            this.toastrService.success(this.translateService.instant('page.assortment.added_to_assortment'));
        });
    }

    public removeFromFavorites(item: SearchResultItem) {
        item.inAssortment = false;

        this.apiService.removeFromAssortment(item.guid).subscribe(() => {
            this.toastrService.success(this.translateService.instant('page.assortment.removed'));
        });
    }

    public openQuestionModal() {
        this.modalService.open(QuestionModalComponent, {size: 'lg', backdrop: 'static'});
    }

    public getHierarchyFilterChoices() {
        if (!this.searchResult || !this.searchResult.hierarchy || !this.searchResult.hierarchy.filter) {
            return [];
        }

        return this.searchResult.hierarchy.filter.choices;
    }

    public openLoginModal() {
        const modalRef = this.modalService.open(LoginModalComponent);

        modalRef.componentInstance.currentRoute = this.router.url;
    }

    public getArticle(item: SearchResultItem): Article {
        return item as Article;
    }

    public changeQuantity(item: SearchResultItem, quantity) {
        quantity = parseInt(quantity);

        if (isNaN(quantity) || quantity === 0) {
            this.setQuantity(item, null);

            return;
        }

        this.setQuantity(item, quantity);
    }
}
